import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

// ClassJoinPage — public landing for /class/:code. Fetches the class metadata
// (teacher name + capacity) so the student can confirm before typing their name.
function ClassJoinPage({ initialClassCode }) {
  const { loginStudent } = useAuth();
  const [code, setCode] = useState((initialClassCode || '').toUpperCase());
  const [name, setName] = useState('');
  const [classInfo, setClassInfo] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Look up the class metadata as soon as we have a 6-char code.
  useEffect(() => {
    if (code.length !== 6) {
      setClassInfo(null);
      return;
    }
    let cancelled = false;
    setError('');
    fetch(`${API_URL}/graphql`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `query($code:String!){classByCode(code:$code){teacherName classCode isFull studentCount studentLimit}}`,
        variables: { code },
      }),
    })
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data.errors?.length) {
          setClassInfo(null);
          setError(data.errors[0].message || 'Class not found.');
          return;
        }
        setClassInfo(data.data?.classByCode || null);
      })
      .catch(() => {
        if (!cancelled) setError('Could not reach the server.');
      });
    return () => { cancelled = true; };
  }, [code]);

  const canSubmit = code.length === 6 && name.trim().length > 0 && !submitting;

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/class/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, studentName: name.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not join the class.');
        setSubmitting(false);
        return;
      }
      loginStudent(data.token, {
        studentName: data.studentName,
        teacherName: data.teacherName,
        classCode: data.classCode,
      });
      // Drop the /class/:code path so the student lands on the main app shell.
      window.history.replaceState({}, '', '/');
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  }, [code, name, canSubmit, loginStudent]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
        background: 'var(--card)',
        border: '1px solid var(--rule)',
        borderRadius: 12,
        padding: '40px 32px 32px',
        boxShadow: 'var(--sh3)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            fontFamily: 'var(--font-han)',
            fontSize: 144,
            lineHeight: 1,
            color: 'var(--vermillion)',
            marginBottom: 24,
            fontWeight: 500,
            letterSpacing: '0.04em',
          }}>
            班
          </div>
          <div className="t-eyebrow" style={{ marginBottom: 8 }}>
            班级 · Class
          </div>
          <h1 className="t-h1" style={{ fontSize: 28, marginBottom: 8 }}>
            Join your class
          </h1>
          {classInfo ? (
            <p className="t-small">
              Joining <strong>{classInfo.teacherName}</strong>'s class
              {' · '}
              {classInfo.studentCount} / {classInfo.studentLimit} students
            </p>
          ) : (
            <p className="t-small">Enter your class code to get started.</p>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="field" style={{ marginBottom: 14 }}>
            <label className="field-label" htmlFor="class-code">Class code</label>
            <input
              id="class-code"
              className="input input--lg input--mono"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 6))}
              style={{ textAlign: 'center', fontSize: 24, letterSpacing: '0.18em' }}
              maxLength={6}
              readOnly={Boolean(initialClassCode)}
              placeholder="ABC234"
              autoFocus={!initialClassCode}
            />
          </div>

          <div className="field" style={{ marginBottom: 24 }}>
            <label className="field-label" htmlFor="student-name">Your name</label>
            <input
              id="student-name"
              className="input input--lg"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={20}
              placeholder="Type your name"
              autoFocus={Boolean(initialClassCode)}
            />
          </div>

          {error && (
            <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 16 }}>
              {error}
            </p>
          )}
          {classInfo?.isFull && !error && (
            <p style={{ color: 'var(--vermillion)', fontSize: 13, marginBottom: 16 }}>
              This class is full. Your teacher needs to upgrade to add more students.
            </p>
          )}

          <button
            type="submit"
            className="btn btn--accent btn--lg btn--block"
            disabled={!canSubmit || (classInfo?.isFull && error === '')}
          >
            {submitting ? 'Joining…' : 'Enter class →'}
          </button>
        </form>

        <p className="t-small" style={{ textAlign: 'center', marginTop: 16 }}>
          No account needed · works on any phone
        </p>
      </div>
    </div>
  );
}

export default ClassJoinPage;
